/**
 * SSO Service for Enterprise Identity Provider Integration.
 *
 * This service handles SSO authentication flows for both OIDC and SAML protocols.
 * It communicates with the BRAIN backend SSO API endpoints.
 *
 * IMPORTANT: This is for SSO AUTHENTICATION (managing user ACCESS to the system).
 * For identity ENRICHMENT, see the backend oauth2_identity_connector module.
 *
 * Supported Identity Providers:
 * - Okta (OIDC + SAML)
 * - Azure AD / Microsoft Entra ID (OIDC + SAML)
 * - Google Workspace (OIDC)
 * - Generic OIDC/SAML providers
 */

import { getBaseUrl } from '../../../shared/utils/apiConfig';
import { AUTH_CONFIG } from '../../../config';

// SSO Protocol types
export type SSOProtocol = 'oidc' | 'saml';

// SSO Provider types matching backend
export type SSOProviderType =
  | 'okta'
  | 'azure_ad'
  | 'google_workspace'
  | 'onelogin'
  | 'ping_identity'
  | 'auth0'
  | 'generic_oidc'
  | 'generic_saml';

// SSO Configuration status
export type SSOConfigStatus = 'draft' | 'testing' | 'active' | 'disabled' | 'archived';

/**
 * SSO Provider configuration returned from API
 */
export interface SSOProvider {
  id: string;
  name: string;
  protocol: SSOProtocol;
  provider_type: SSOProviderType;
  login_url: string;
}

/**
 * SSO Configuration response from API
 */
export interface SSOConfig {
  id: string;
  name: string;
  protocol: SSOProtocol;
  provider_type: SSOProviderType;
  status: SSOConfigStatus;
  is_primary: boolean;
  callback_url?: string;
  created_at: string;
  last_used_at?: string;
}

/**
 * User data returned after successful SSO authentication
 */
export interface SSOUser {
  id: string;
  email: string;
  username: string;
  full_name: string;
  roles: string[];
}

/**
 * Token response after successful SSO authentication
 */
export interface SSOTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  sso_session_id: string;
  user: SSOUser;
}

/**
 * SSO Statistics
 */
export interface SSOStats {
  active_sessions: number;
  successful_logins_24h: number;
  failed_logins_24h: number;
}

/**
 * SSO Service class for managing SSO authentication
 */
class SSOService {
  private baseUrl: string;
  private tenantId: string | null = null;

  constructor() {
    this.baseUrl = `${getBaseUrl()}/api/auth/sso`;
  }

  /**
   * Set the tenant ID for SSO operations
   */
  setTenantId(tenantId: string): void {
    this.tenantId = tenantId;
  }

  /**
   * Get tenant ID from various sources
   */
  getTenantId(): string | null {
    if (this.tenantId) return this.tenantId;

    // Try localStorage
    const stored = localStorage.getItem('tenant_id');
    if (stored) return stored;

    // Try to extract from hostname
    const hostname = window.location.hostname;
    if (hostname && hostname !== 'localhost') {
      const subdomain = hostname.split('.')[0];
      if (!['app', 'api', 'www'].includes(subdomain)) {
        return subdomain;
      }
    }

    return null;
  }

  /**
   * Get headers with tenant ID
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const tenantId = this.getTenantId();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }

    return headers;
  }

  /**
   * Get available SSO providers for the tenant
   */
  async getProviders(): Promise<SSOProvider[]> {
    try {
      const response = await fetch(`${this.baseUrl}/providers`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SSO providers: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching SSO providers:', error);
      return [];
    }
  }

  /**
   * Get all SSO configurations for the tenant
   */
  async getConfigs(protocol?: SSOProtocol): Promise<SSOConfig[]> {
    try {
      let url = `${this.baseUrl}/config`;
      if (protocol) {
        url += `?protocol=${protocol}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SSO configs: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching SSO configs:', error);
      return [];
    }
  }

  /**
   * Initiate SSO login flow
   *
   * This redirects the user to the identity provider for authentication.
   * After authentication, the IdP will redirect back to the callback URL.
   *
   * @param protocol - 'oidc' or 'saml'
   * @param loginHint - Optional email/username to pre-fill at IdP
   * @param configId - Optional specific SSO config ID to use
   * @param invitationToken - Sprint 4: Optional auditor invitation token
   */
  initiateLogin(
    protocol: SSOProtocol,
    loginHint?: string,
    configId?: string,
    invitationToken?: string
  ): void {
    let url = `${this.baseUrl}/${protocol}/login`;

    const params = new URLSearchParams();
    if (loginHint) {
      params.append('login_hint', loginHint);
    }
    if (configId) {
      params.append('config_id', configId);
    }
    if (invitationToken) {
      params.append('invitation_token', invitationToken);
    }

    const tenantId = this.getTenantId();
    if (tenantId) {
      params.append('tenant_id', tenantId);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    // Redirect to SSO login endpoint
    window.location.href = url;
  }

  /**
   * Handle SSO callback (called when returning from IdP)
   *
   * This is typically called from the callback page component.
   * For OIDC, the callback has ?code=xxx&state=xxx parameters.
   * For SAML, the callback is a POST with SAMLResponse.
   */
  async handleCallback(): Promise<SSOTokenResponse | null> {
    const urlParams = new URLSearchParams(window.location.search);

    // Check for errors
    const error = urlParams.get('error');
    if (error) {
      const errorDescription = urlParams.get('error_description') || error;
      throw new Error(`SSO authentication failed: ${errorDescription}`);
    }

    // For OIDC callback, the backend handles the token exchange
    // The response is returned directly in the page content
    // This is handled by the callback page component

    return null;
  }

  /**
   * Process SSO tokens and store authentication state
   *
   * @param tokenResponse - Token response from SSO callback
   */
  processTokenResponse(tokenResponse: SSOTokenResponse): void {
    // Store tokens
    localStorage.setItem('astrapeak_auth_token', tokenResponse.access_token);
    localStorage.setItem(AUTH_CONFIG.refreshTokenStorageKey, tokenResponse.refresh_token);
    localStorage.setItem('sso_session_id', tokenResponse.sso_session_id);

    // Store user info
    localStorage.setItem('user', JSON.stringify(tokenResponse.user));

    // Set authed flag
    sessionStorage.setItem('authed', 'true');

    // Store token expiry
    const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
    localStorage.setItem('token_expires_at', expiresAt.toString());
  }

  /**
   * Logout from SSO session
   *
   * @param initiateSLO - Whether to initiate Single Logout with IdP
   */
  async logout(initiateSLO: boolean = true): Promise<string | null> {
    const ssoSessionId = localStorage.getItem('sso_session_id');

    if (!ssoSessionId) {
      // No SSO session, just clear local state
      this.clearLocalAuth();
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/logout`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          sso_session_id: ssoSessionId,
          initiate_slo: initiateSLO,
        }),
      });

      if (!response.ok) {
        throw new Error(`Logout failed: ${response.status}`);
      }

      const data = await response.json();

      // Clear local auth state
      this.clearLocalAuth();

      // Return SLO URL if present (for redirect to IdP logout)
      return data.slo_url || null;
    } catch (error) {
      console.error('Error during SSO logout:', error);
      // Still clear local state even if backend call fails
      this.clearLocalAuth();
      return null;
    }
  }

  /**
   * Clear local authentication state
   */
  clearLocalAuth(): void {
    localStorage.removeItem('astrapeak_auth_token');
    localStorage.removeItem(AUTH_CONFIG.refreshTokenStorageKey);
    localStorage.removeItem('sso_session_id');
    localStorage.removeItem('user');
    localStorage.removeItem('token_expires_at');
    sessionStorage.removeItem('authed');
  }

  /**
   * Check if user is authenticated via SSO
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('astrapeak_auth_token');
    const expiresAt = localStorage.getItem('token_expires_at');

    if (!token) return false;

    if (expiresAt) {
      const expiry = parseInt(expiresAt, 10);
      if (Date.now() > expiry) {
        // Token expired
        return false;
      }
    }

    return true;
  }

  /**
   * Get current user info
   */
  getCurrentUser(): SSOUser | null {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;

    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }

  /**
   * Get SSO statistics (admin only)
   */
  async getStats(): Promise<SSOStats | null> {
    try {
      const response = await fetch(`${this.baseUrl}/stats`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SSO stats: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching SSO stats:', error);
      return null;
    }
  }

  /**
   * Get provider display info (icon, name)
   */
  getProviderDisplayInfo(providerType: SSOProviderType): {
    name: string;
    icon: string;
    color: string;
  } {
    const providers: Record<SSOProviderType, { name: string; icon: string; color: string }> = {
      okta: { name: 'Okta', icon: 'okta', color: '#007dc1' },
      azure_ad: { name: 'Microsoft', icon: 'microsoft', color: '#00a4ef' },
      google_workspace: { name: 'Google', icon: 'google', color: '#4285f4' },
      onelogin: { name: 'OneLogin', icon: 'onelogin', color: '#00bcd4' },
      ping_identity: { name: 'Ping Identity', icon: 'ping', color: '#b4d455' },
      auth0: { name: 'Auth0', icon: 'auth0', color: '#eb5424' },
      generic_oidc: { name: 'SSO', icon: 'key', color: '#6366f1' },
      generic_saml: { name: 'SSO', icon: 'shield', color: '#8b5cf6' },
    };

    return providers[providerType] || providers.generic_oidc;
  }
}

// Export singleton instance
export const ssoService = new SSOService();

// Export class for testing
export { SSOService };
